/**
 * Phase 8 T-20 §24.A — Step 1: Booking Link
 *
 * Displays the recruiter's shareable booking URL with:
 *   - URL preview (handle + slug)
 *   - [Copy link] button (clipboard)
 *   - [Customise slug] inline edit
 *   - Preview → opens candidate-facing stub in new tab
 */

import { useCallback, useState } from 'react';
import { Check, Copy, ExternalLink, Pencil } from 'lucide-react';
import type { BookingConfig } from './BookingFlow';

const BASE_URL = 'https://app.gograbjob.com/book';

interface BookingLinkStepProps {
  config: BookingConfig;
  onChange: (patch: Partial<BookingConfig>) => void;
}

export function BookingLinkStep({ config, onChange }: BookingLinkStepProps) {
  const [copied, setCopied] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugDraft, setSlugDraft] = useState(config.slug);

  const fullUrl = `${BASE_URL}/${config.handle}/${config.slug}`;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }, [fullUrl]);

  const commitSlug = useCallback(() => {
    const safe = slugDraft
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    const final = safe || config.slug;
    setSlugDraft(final);
    onChange({ slug: final });
    setEditingSlug(false);
  }, [slugDraft, config.slug, onChange]);

  const handleSlugKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') commitSlug();
      if (e.key === 'Escape') {
        setSlugDraft(config.slug);
        setEditingSlug(false);
      }
    },
    [commitSlug, config.slug],
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-1 text-[13px] font-semibold vscode-text-primary">Your booking link</h3>
        <p className="text-[12px] vscode-text-tertiary">
          Share this link with candidates to let them self-schedule interviews.
        </p>
      </div>

      {/* URL display */}
      <div className="rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary">
        <p className="break-all font-mono text-[12px] vscode-text-secondary">
          <span className="vscode-text-tertiary">{BASE_URL}/{config.handle}/</span>
          <span className="font-semibold vscode-text-primary">{config.slug}</span>
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-[#10B981]" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
          {copied ? 'Copied!' : 'Copy link'}
        </button>

        <button
          type="button"
          onClick={() => {
            setSlugDraft(config.slug);
            setEditingSlug(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-[8px] border px-3 py-1.5 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          Customise slug
        </button>

        <a
          href="/book/demo"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[13px] text-[#10B981] transition-opacity hover:opacity-80"
        >
          Preview
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>

      {/* Inline slug editor */}
      {editingSlug && (
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium vscode-text-secondary">
            Custom slug
            <span className="ml-1 font-normal vscode-text-tertiary">
              (letters, numbers and hyphens only)
            </span>
          </label>
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-[12px] font-mono vscode-text-tertiary">
              {BASE_URL}/{config.handle}/
            </span>
            <input
              autoFocus
              type="text"
              value={slugDraft}
              onChange={(e) => setSlugDraft(e.target.value)}
              onKeyDown={handleSlugKey}
              onBlur={commitSlug}
              className="min-w-0 flex-1 rounded-[6px] border px-2 py-1.5 font-mono text-[13px] vscode-border vscode-bg-secondary vscode-text-primary focus:outline-none focus:ring-1 focus:ring-[#10B981]"
              placeholder="my-booking-page"
            />
          </div>
          <p className="text-[11px] vscode-text-tertiary">
            Press Enter to save, Esc to cancel.
          </p>
        </div>
      )}
    </div>
  );
}
